import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  errorMessage,
  fetchExpiryTasks,
  setCrmStage,
  type CrmStage,
  type CrmStageResponse,
} from "../Worklist/api";
import TierSection from "../Worklist/TierSection";
import {
  buildRetention,
  summarizeRetention,
  type RetentionItem,
} from "./pipeline";
import RetentionCard from "./RetentionCard";

/**
 * The Retention CRM section — a SEPARATE surface from the worklist. Expiring/lapsed
 * subscriptions presented as a renewal pipeline (At risk → Contacted → Replied → Call
 * back), draft-first. It is deliberately NOT another worklist lane: the fire-queue stays
 * about service SLA; this is the proactive, outbound counterpart.
 *
 * SAFETY: nothing on this screen sends a message. Every card action either opens
 * waplify's Care Manager chat (a link a human sends from) or records a pipeline stage
 * (picard crm-stage/, a pure DB write). There is no auto-outreach anywhere.
 */

type StageVars = { item: RetentionItem; stage: CrmStage };

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <li
      data-testid="retention-empty"
      className="px-5 py-5 text-center text-sm text-clinic-500 dark:text-gray-400"
    >
      {children}
    </li>
  );
}

function LoadingSection() {
  return (
    <>
      <li className="sr-only" role="status">
        Loading…
      </li>
      {[0, 1].map((i) => (
        <li
          key={i}
          aria-hidden="true"
          className="flex items-center gap-4 border-l-4 border-clinic-200 px-4 py-2.5 dark:border-navy-700"
        >
          <div className="h-8 w-14 shrink-0 animate-pulse rounded bg-clinic-100 motion-reduce:animate-none dark:bg-navy-700" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/3 animate-pulse rounded bg-clinic-100 motion-reduce:animate-none dark:bg-navy-700" />
            <div className="h-3 w-2/3 animate-pulse rounded bg-clinic-100 motion-reduce:animate-none dark:bg-navy-700" />
          </div>
        </li>
      ))}
    </>
  );
}

function ErrorSection({ onRetry }: { onRetry: () => void }) {
  return (
    <li role="alert" className="px-5 py-5 text-center">
      <p className="text-sm font-medium text-clinic-900 dark:text-white">
        Couldn&rsquo;t load the renewal pipeline.
      </p>
      <p className="mt-1 text-xs text-clinic-500 dark:text-gray-400">
        Usually a dropped connection. Retry — if it keeps failing, check that picard is
        reachable.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 touch-manipulation rounded-md bg-action-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-action-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2 motion-reduce:transition-none"
      >
        Retry
      </button>
    </li>
  );
}

function Stat({
  value,
  label,
  warn = false,
  testId,
}: {
  value: string;
  label: string;
  warn?: boolean;
  testId?: string;
}) {
  return (
    <div data-testid={testId} className="min-w-0 flex-1 px-5 py-3">
      <div
        className={`text-[22px] font-bold tabular-nums leading-none ${
          warn ? "text-warn-600 dark:text-amber-400" : "text-clinic-900 dark:text-white"
        }`}
      >
        {value}
      </div>
      <div className="mt-1.5 whitespace-nowrap font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-clinic-500 dark:text-gray-400">
        {label}
      </div>
    </div>
  );
}

export default function Retention() {
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const tasks = useQuery({
    queryKey: ["retention", "tasks"],
    queryFn: fetchExpiryTasks,
  });

  const groups = useMemo(
    () => buildRetention(tasks.data ?? [], Date.now()),
    [tasks.data]
  );
  const summary = useMemo(() => summarizeRetention(groups), [groups]);

  const stageMut = useMutation<CrmStageResponse, unknown, StageVars>({
    mutationFn: ({ item, stage }: StageVars): Promise<CrmStageResponse> =>
      setCrmStage(item.taskId, stage),
    onMutate: ({ item }: StageVars) => {
      setErrors((prev) => {
        if (!(item.key in prev)) return prev;
        const next = { ...prev };
        delete next[item.key];
        return next;
      });
    },
    onSuccess: () => {
      // The task's stage changed (or, for a terminal outcome, it closed server-side) —
      // refetch so it re-groups or drops out.
      void queryClient.invalidateQueries({ queryKey: ["retention"] });
    },
    onError: (err: unknown, { item }: StageVars) => {
      setErrors((prev) => ({ ...prev, [item.key]: errorMessage(err) }));
    },
  });

  const pendingKey =
    stageMut.isPending && stageMut.variables ? stageMut.variables.item.key : null;

  const onStage = useCallback(
    (item: RetentionItem, stage: CrmStage) => stageMut.mutate({ item, stage }),
    [stageMut]
  );

  return (
    <div className="mx-auto max-w-6xl px-6 pt-5 pb-12">
      <header className="mb-5">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-clinic-400 dark:text-gray-500">
          Babynama Clinic-OS
        </p>
        <h1 className="mt-0.5 text-[22px] font-bold leading-tight tracking-tight text-clinic-900 [text-wrap:balance] dark:text-white">
          Retention
        </h1>
        <p className="mt-0.5 text-sm text-clinic-500 dark:text-gray-400">
          Expiring and lapsed subscriptions as a renewal pipeline — proactive, outbound,
          separate from the SLA worklist.
        </p>

        {/*
          DRAFT-FIRST, made unmistakable up front: this whole screen is outreach a HUMAN
          performs in waplify. Nothing here auto-sends; the outcome buttons only record
          where each customer sits in the pipeline.
        */}
        <div
          data-testid="retention-safety"
          className="mt-3 flex items-start gap-2 rounded-lg border border-warn-200 bg-warn-50 px-3 py-2 text-xs text-warn-700 dark:border-navy-700 dark:!bg-navy-800 dark:text-amber-200"
        >
          <span aria-hidden="true" className="mt-px font-semibold">
            Draft-first
          </span>
          <span>
            Nothing on this screen sends a message. Open the customer in waplify and send
            the draft yourself; the outcome buttons only record their pipeline stage.
          </span>
        </div>

        <div
          data-testid="retention-summary"
          className="mt-4 flex flex-wrap items-stretch divide-x divide-clinic-100 rounded-lg border border-clinic-200 bg-white shadow-sm dark:divide-navy-700 dark:border-navy-700 dark:!bg-navy-800"
        >
          {tasks.isLoading ? (
            <span className="px-5 py-4 text-sm text-clinic-500 dark:text-gray-400">
              Loading…
            </span>
          ) : (
            <>
              <Stat testId="ret-total" value={String(summary.total)} label="in pipeline" />
              <Stat
                testId="ret-expiring"
                value={String(summary.expiringToday)}
                label="expire today"
                warn={summary.expiringToday > 0}
              />
              <Stat
                testId="ret-savable"
                value={String(summary.savable)}
                label="still savable"
                warn={summary.savable > 0}
              />
              <Stat testId="ret-lapsed" value={String(summary.lapsed)} label="already lapsed" />
            </>
          )}
        </div>
      </header>

      {groups.map((group) => (
        <TierSection
          key={group.stage}
          id={`retention-${group.stage}`}
          label={group.label}
          blurb={group.blurb}
          count={group.items.length}
          accent="muted"
        >
          {tasks.isLoading && <LoadingSection />}
          {!tasks.isLoading && tasks.isError && (
            <ErrorSection onRetry={() => void tasks.refetch()} />
          )}
          {!tasks.isLoading && !tasks.isError && group.items.length === 0 && (
            <Notice>No customers in this stage.</Notice>
          )}
          {!tasks.isLoading &&
            !tasks.isError &&
            group.items.map((item) => (
              <RetentionCard
                key={item.key}
                item={item}
                onStage={onStage}
                pending={pendingKey === item.key}
                error={errors[item.key] ?? null}
              />
            ))}
        </TierSection>
      ))}
    </div>
  );
}
